import { h, ref } from 'vue'
import { message, Modal } from 'ant-design-vue'
import { safeParseJson } from '@bd/vue3-utils'
import Axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, RawAxiosRequestHeaders } from 'axios'
import { PicVerify } from '-/Business'
import { useUserStoreOutside } from '@/store/modules/user'
import { router, resloveToLoginPath } from '@/router'
import { apiDebugger, genHeaders } from './tools'
import {
  CONTENT_TYPE,
  ERR_PIC_VERIFY,
  EXPIRED_CODE,
  NEED_CONFIRM_CODE,
  OK_CODE,
  OK_STATUS,
  PIC_VERIFY_CODE,
  WARNING_CODE,
  baseURL,
  timeout
} from './constant'

interface CreateCtx {
  header?: RawAxiosRequestHeaders
  reqConfig?: AxiosRequestConfig
}
export const createAxios = (params: CreateCtx = {}) => {
  const {
    header = {
      'Content-Type': CONTENT_TYPE.JSON
    },
    reqConfig = {}
  } = params
  const axios: AxiosInstance = Axios.create({
    baseURL,
    withCredentials: false,
    timeout,
    ...reqConfig
  })

  /**
   * 前置拦截器（发起请求之前的拦截）
   */
  axios.interceptors.request.use(
    config => {
      Object.assign(config.headers, {
        ...genHeaders(),
        ...header
      })
      return config
    },
    error => {
      return Promise.reject(error)
    }
  )

  // 响应处理器接口
  interface ResponseHandler {
    canHandle(response: any, status: number): boolean
    handle(response: any, axios: AxiosInstance): Promise<any>
  }
  // 警告信息处理器
  class WarningHandler implements ResponseHandler {
    canHandle(response: any): boolean {
      return response.data.code === WARNING_CODE
    }

    async handle(response: any): Promise<any> {
      message.warning(response.data.message || '警告信息')
      return Promise.resolve(response.data)
    }
  }
  // 确认操作处理器
  class ConfirmHandler implements ResponseHandler {
    canHandle(response: any): boolean {
      return response.data.code === NEED_CONFIRM_CODE
    }

    async handle(response: any, axios: AxiosInstance): Promise<any> {
      return new Promise((resolve, reject) => {
        Modal.confirm({
          centered: true,
          title: '请确认操作',
          content: response.data.message || '操作确认',
          onOk() {
            const { data } = response.config
            const _data = safeParseJson(data)
            const newData = { ..._data, confirm: true }
            // 直接返回 Promise，让 Modal 处理 loading 状态
            return axios({ ...response.config, data: newData })
              .then(resolve)
              .catch(reject)
          },
          onCancel() {
            reject(response.data)
          }
        })
      })
    }
  }

  // 图片验证码处理器
  class PicVerifyHandler implements ResponseHandler {
    canHandle(response: any): boolean {
      return response.data.code === PIC_VERIFY_CODE
    }

    async handle(response: any): Promise<any> {
      return new Promise((resolve, reject) => {
        const picVerifyRef = ref()
        const content = () =>
          h(PicVerify, {
            ref: picVerifyRef,
            style: { marginTop: '16px' }
          })
        Modal.confirm({
          centered: true,
          title: '请输入验证码',
          content,
          onOk(close) {
            const { uuid, value, refresh } = picVerifyRef.value
            if (!uuid || !value) {
              message.warning('请输入验证码')
              return
            }
            const { data } = response.config
            const _data = safeParseJson(data)
            const newData = { ..._data, picVerifyCode: value, picUuid: uuid }
            axios({ ...response.config, data: newData })
              .then(res => {
                close()
                resolve(res)
              })
              .catch(err => {
                if (err.code === ERR_PIC_VERIFY) {
                  refresh()
                } else {
                  close()
                  reject(err)
                }
              })
          },
          onCancel() {
            reject(response.data)
          }
        })
      })
    }
  }

  // 登录过期处理器
  class ExpiredHandler implements ResponseHandler {
    canHandle(response: any): boolean {
      return EXPIRED_CODE.includes(response.data.code)
    }

    async handle(response: any): Promise<any> {
      const userStore = useUserStoreOutside()
      message.warning('登录超时')
      userStore.logout()
      const _loginPath = resloveToLoginPath(router.currentRoute.value.fullPath)
      router.replace(_loginPath)
      return Promise.reject(response)
    }
  }

  // 响应处理器链
  class ResponseHandlerChain {
    private handlers: ResponseHandler[] = [new ConfirmHandler(), new PicVerifyHandler(), new ExpiredHandler(), new WarningHandler()]

    async handle(response: any, status: number, axios: AxiosInstance): Promise<any> {
      apiDebugger(response)
      const res = response.data

      if (status !== OK_STATUS) {
        message.error(res.message || '服务异常')
        return Promise.reject(res)
      }

      if (res instanceof Blob) {
        return res
      }

      if (res.code !== OK_CODE) {
        for (const handler of this.handlers) {
          if (handler.canHandle(response, status)) {
            return handler.handle(response, axios)
          }
        }
        message.error(res.message || '系统异常')
        return Promise.reject(res)
      }

      return res.data
    }
  }

  const responseHandlerChain = new ResponseHandlerChain()

  /**
   * 后置拦截器（获取到响应时的拦截）
   */
  axios.interceptors.response.use(
    async response => {
      return responseHandlerChain.handle(response, response.status, axios)
    },
    error => {
      apiDebugger(error)
      if (Axios.isCancel(error)) return error
      message.error(`网络异常 ${error?.message ?? '未知错误'}`)
      return Promise.reject(error)
    }
  )
  return axios
}
